import { CriteriaObject } from '../../models/dto/criteria-object.model';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EmailListResponse, Message, PaginatedThread, Thread } from './message.model';
import { ApiHttpClient } from '../../http/clients/api';
import { environment } from '../../../environments/environment';
import { EmailTemplate } from 'app/models/dto/email-template.model';
import { TemplateVariable } from './template-variable.model';
import { EmailDirectoryListItem } from 'app/models/dto/directory-list-item.model';

interface ThreadsPaginationResponse {
  data: Thread[];
  current_page: number;
  from: number;
  to: number;
  total: number;
}

export interface TodayCounts {
  unreadEmailThreads?: number;
  unknownUnreadEmailThreads?: number;
  unreadSmsThreads?: number;
  unreadUnknownSmsThreads?: number;
  unreadEmailCountForThread?: number;
  unreadSmsCountForThread?: number;
}

const unknownMessagesRoute = '/thread/unknown-messages';
@Injectable({
  providedIn: 'root',
})

export class MessageService {

  defaultThreadFetchLimit = 5;

  constructor(private http: ApiHttpClient) { }

  send(message: Message): Observable<Thread> {
    return this.http.post<Thread>(`${environment.API_URL}/thread`, message);
  }

  previewMessage(message: Message) {
    return this.http.post(`${environment.API_URL}/message/preview`, message);
  }

  sendBulkMessages(query, payload): Observable<[]> {
    return this.http.post<[]>('/message',  payload, {
      params: {query: JSON.stringify(query)}
    });
  }

  put(id: number, message: Message): Observable<Thread> {
    return this.http.put<Thread>(`${environment.API_URL}/thread/${id}`, message);
  }

  messagePut(id: number, updates: any): Observable<Thread> {
    return this.http.put<Thread>(`${environment.API_URL}/message/${id}`, updates);
  }

  messageRead(id): Observable<Thread> {
    return this.http.put<Thread>(`${environment.API_URL}/message-read/${id}`, {});
  }

  getMessageVariables(borrowerId: number): Observable<any> {
    return this.http.get(`${environment.API_URL}/message/variables/${borrowerId}`);
  }

  getEmailSignature(borrowerId: number): Observable<any> {
    return this.http.get(`${environment.API_URL}/message/signature/${borrowerId}`);
  }

  getTemplateVariables(): Observable<TemplateVariable[]> {
    return this.http.get<TemplateVariable[]>(`${environment.API_URL}/message/variables`);
  }

  getTemplate(id): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(`${environment.API_URL}/template/${id}`);
  }

  getTemplates(): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[]>(`${environment.API_URL}/template`);
  }

  createTemplate(template: { content: string, title: string, subject: string }): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(`${environment.API_URL}/template`, template);
  }

  updateTemplate(template: { id: number, content: string, title: string, subject: string }): Observable<EmailTemplate> {
    return this.http.put<EmailTemplate>(`${environment.API_URL}/template/${template.id}`, template);
  }

  deleteTemplate(templateId: number): Observable<any> {
    return this.http.delete<any>(`${environment.API_URL}/template/${templateId}`);
  }

  getMessageThreadHistory(borrowerId: number, retrieveAll = false, limit = null): Observable<Thread[]> {
    const queryParams: CriteriaObject = {
      criteria: {
        'borrowerId': { '==': borrowerId }
      }
    };
    if (!retrieveAll) {
      queryParams.limit = limit || this.defaultThreadFetchLimit;
      queryParams.sort = {
        'threads.modified': 'DESC'
      };
    }
    const query = encodeURIComponent(JSON.stringify(queryParams));
    return this.http.get<Thread[]>(`${environment.API_URL}/thread?query=${query}`);
  }

  getUnknownThreads(lenderUserId: number, nextUrl?): Observable<PaginatedThread> {
    const queryParams: CriteriaObject = {
      criteria: {
        'borrowerId': { '==': 'null' },
        'lenderUserId': { '==': lenderUserId },
      },
      sort: {
        'threads.created': 'ASC'
      }
    };
    const query = encodeURIComponent(JSON.stringify(queryParams));
    if(nextUrl) {
      return this.http.get<PaginatedThread>(`${environment.API_URL}${this.getRoute(nextUrl)}&query=${query}`);
    }
    return this.http.get<PaginatedThread>(`${environment.API_URL}${unknownMessagesRoute}?query=${query}`);
  }

  deleteMessageFromUnknownSender(messageId: number): Observable<any> {
    return this.http.delete<any>(`${environment.API_URL}/message/${messageId}`);
  }

  getBorrowerEmailList(filterBy: string, limit: number, offset: number, sort: string, borrowerId: number): Observable<EmailListResponse[]> {

    const options = {
      params: {
        filterBy,
        limit,
        offset,
        sort,
        borrowerId
      }
    };

    return this.http.get<EmailListResponse[]>(`${environment.API_URL}/email-list`, options);
  }

  getRoute(absoluteUrl) {
    return absoluteUrl.slice(absoluteUrl.indexOf(unknownMessagesRoute));
  }

  getUserThreads( lenderUserId, page, types, sortDirection ) {
    const options = {
      params: {
        page,
        sortDirection,
        filterBy: JSON.stringify(types),
      }
    };
    return this.http.get<ThreadsPaginationResponse>(`${environment.API_URL}/user-threads/${lenderUserId}`, options);
  }

  getSingleThread(threadId) {
    return this.http.get<Thread>(`${environment.API_URL}/thread/${threadId}`);
  }

  addBorrowerToThread( borrowerId, threadId ) {
    const options = {
      params: {
        borrowerId
      }
    };
    return this.http.post(`${environment.API_URL}/thread/${threadId}/add-borrower`, options);
  }

  markThreadRead( threadId: number ) {
    return this.http.post<TodayCounts>(`${environment.API_URL}/thread/mark-read/${threadId}`, {});
  }

  markThreadUnread( threadId: number ) {
    return this.http.post<TodayCounts>(`${environment.API_URL}/thread/mark-unread/${threadId}`, {});
  }

  getLendioEmailDirectory(): Observable<EmailDirectoryListItem[]> {
    return this.http.get<EmailDirectoryListItem[]>('/lendio-email-directory');
  }
}
